import { FOLDER_SELECT_MENU_HEIGHT, MAX_TITLE_LENGTH, MIN_TITLE_LENGTH } from '@opengov/stories-shared/constants';
import { IPublication } from '@opengov/stories-shared/types';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import FormControlLabel from '@mui/material/FormControlLabel';
import CloseIcon from '@mui/icons-material/Close';
import FormHelperText from '@mui/material/FormHelperText';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useEffect, useMemo, useState } from 'react';

import { IPublicationViewMessages, ISessionUser, IStoriesFolder } from '../../../../types';
import { FolderTreeSelect } from '../../CommonComponents/FolderTreeSelect/FolderTreeSelect';

import { useCreateSection, useUpdatePublicationUpdatedAt } from '../PublicationView/hooks/usePublicationMutations';
import { useToast } from '../../Toast/context/useToast.context';
import { navigateToPublicationFolderList } from '../../../state/navigation';
import { transformFolderStructureToMap } from '../../../utils';

import { styles } from './AddSectionModal.styles';

export interface IAddSectionModalProps {
  isAdmin: boolean;
  messages: IPublicationViewMessages;
  folderStructure: IStoriesFolder[];
  user: ISessionUser;
  entityId: number;
  publication: IPublication;
  rootFolderId: string;
  selectedSectionId: string;
  entitySubdomain: string;
  onClose(): void;
}

enum IAddSectionType {
  NEW_SUB_SECTION = 'newSubSection',
  NEW_SECTION = 'subSection'
}

export const AddSectionModal = (props: IAddSectionModalProps) => {
  const {
    onClose,
    messages,
    folderStructure,
    publication,
    user,
    entityId,
    rootFolderId,
    selectedSectionId,
    entitySubdomain
  } = props;

  const { toggleToast } = useToast();
  const createSectionMutation = useCreateSection(
    { title: messages.somethingWentWrongError, body: messages.failedToCreatePublicationSection },
    messages.noSectionSameName
  );
  const updateTimestampMutation = useUpdatePublicationUpdatedAt();

  const [sectionName, setSectionName] = useState('');
  const [isSelected, setDefaultSelected] = useState(!selectedSectionId);
  const [folderId, setFolderId] = useState(!!selectedSectionId ? selectedSectionId : folderStructure[0]?.id);
  const [isSubSection, setSubSection] = useState(!!selectedSectionId);
  const [isTooDeep, setIsTooDeep] = useState(false);
  const folderMap = useMemo(() => transformFolderStructureToMap(folderStructure), [folderStructure]);
  const trimmedSectionName = useMemo(() => sectionName.trim(), [sectionName]);

  useEffect(() => {
    if (isSubSection && folderMap[folderId]) {
      // Prevent folder creation if depth is > 6
      setIsTooDeep(folderMap[folderId].depth > 6);
    } else {
      setIsTooDeep(false);
    }
  }, [isSubSection, folderId]);

  const handleAddSection = async () => {
    try {
      const { sectionId } = await createSectionMutation.mutateAsync({
        userId: user.id,
        folderTitle: trimmedSectionName,
        entityId: entityId,
        parentFolderId: isSubSection ? folderId : rootFolderId,
        publicationId: publication.id,
        isSubSection: isSubSection
      });
      updateTimestampMutation.mutate({ publicationId: publication.id, userId: user.id });
      navigateToPublicationFolderList(entitySubdomain, publication.id, sectionId);
      toggleToast({
        title: messages.sectionCreated,
        body: messages.sectionCreatedDescription,
        severity: 'success'
      });
    } catch {
      // Error toast emitted by useCreateSection (uniqueness → noSectionSameName).
    } finally {
      onClose();
    }
  };

  const handleSectionOption = (value: IAddSectionType): void => {
    setSubSection(false);
    setDefaultSelected(true);
    if (value === IAddSectionType.NEW_SUB_SECTION) {
      setDefaultSelected(false);
      setSubSection(true);
    }
  };

  return (
    <Dialog open={true} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: styles.dialogPaper }} aria-labelledby="add-section-dialog-title">
      <DialogTitle id="add-section-dialog-title" sx={styles.dialogTitle}>
        {messages.addNewSelection}
        <IconButton aria-label="close" onClick={onClose} size="small" sx={styles.closeButton}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.dialogContent}>
        <Box data-test="add-section-form">
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              aria-labelledby="add-section-dialog-title"
              sx={styles.radioGroup}
              value={isSubSection ? IAddSectionType.NEW_SUB_SECTION : IAddSectionType.NEW_SECTION}
              onChange={(_e, value) => handleSectionOption(value as IAddSectionType)}
            >
              <FormControlLabel
                sx={styles.radioButton}
                value={IAddSectionType.NEW_SECTION}
                control={
                  <Radio
                    inputProps={{ 'data-test': 'add-new-section-option', className: 'no-format' } as React.InputHTMLAttributes<HTMLInputElement>}
                    name="newSection"
                  />
                }
                label={messages.newSectionLabel}
                checked={isSelected}
              />
              <FormControlLabel
                sx={styles.radioButton}
                value={IAddSectionType.NEW_SUB_SECTION}
                control={
                  <Radio
                    inputProps={{ 'data-test': 'new-sub-section', name: 'newSubSection', className: 'no-format' } as React.InputHTMLAttributes<HTMLInputElement>}
                    name="newSubSection"
                  />
                }
                label={messages.newSubSectionLabel}
                checked={folderId?.length > 0 && isSubSection}
                disabled={!folderStructure?.length}
              />
            </RadioGroup>
          </FormControl>

          <Box>
            <Box sx={styles.labelRow}>
              <Typography id="add-section-name-label" variant="body2" sx={styles.formLabel}>
                {messages.sectionFormLabel}
              </Typography>
              <Typography variant="caption" sx={styles.characterCounter}>
                {sectionName.length}/{MAX_TITLE_LENGTH}
              </Typography>
            </Box>
            <TextField
              fullWidth
              size="small"
              sx={styles.textField}
              placeholder={messages.sectionNamePlaceholder}
              value={sectionName}
              onChange={(e) => setSectionName(e.target.value)}
              inputProps={{ 'data-test': 'section-title-input', maxLength: MAX_TITLE_LENGTH, className: 'no-format', 'aria-labelledby': 'add-section-name-label' }}
            />
          </Box>

          {isSubSection && (
            <Box sx={styles.listItem}>
              <Typography variant="body2" sx={styles.formLabel}>
                {messages.location}
              </Typography>
              <FolderTreeSelect
                activeNodeId={folderId}
                data={folderStructure}
                dataTest="folder-accordion-select"
                disabled={!folderStructure?.length}
                maxMenuHeight={FOLDER_SELECT_MENU_HEIGHT}
                onNodeSelect={(node) => setFolderId(node.id)}
                showFilter={true}
                filterPlaceholder={messages.filterPlaceHolder}
              />
            </Box>
          )}

          {isTooDeep && (
            <FormHelperText error sx={styles.errorMsg}>
              {messages.nestedTooDeep}
            </FormHelperText>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={styles.dialogActions}>
        <Button variant="outlined" color="secondary" size="small" onClick={onClose} data-test="cancel-add-section-button">
          {messages.cancel}
        </Button>
        <Button
          variant="contained"
          size="small"
          color="primary"
          disabled={
            trimmedSectionName.length < MIN_TITLE_LENGTH || trimmedSectionName.length > MAX_TITLE_LENGTH || isTooDeep
          }
          onClick={() => {
            handleAddSection();
          }}
          data-test="add-section-button"
        >
          {messages.add}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
